import { generatePath, matchPath } from 'react-router-dom';

function joinPath(parent, child) {
  if (child.startsWith('/')) {
    return child;
  }

  return `${parent.replace(/\/+$/, '')}/${child}`;
}
/**
 * 安全的 URL 拼装：
 * 当模板含有当前 params 里没有的参数（例如在根作用域下给 /tenant/:tenantId 生成 URL）时，
 * generatePath 会抛错，这里降级为 '#'，由调用方决定是否禁用入口。
 */


function toUrl(pathTemplate, params) {
  try {
    return generatePath(pathTemplate, params);
  } catch (error) {
    return '#';
  }
}
/** 按 key 取真实 URL（scope 入口、菜单、面包屑共用同一套推导） */


export function resolveRouteUrl(all, key, params = {}) {
  const route = all.find(item => item.node.key === key);

  if (!route) {
    return '#';
  }

  return toUrl(route.pathTemplate, params);
}
/**
 * 把配置树静态展开为一维节点表，与具体参数值无关。
 * 由于子节点会拼接父节点路径，父节点的 :param 会自然出现在子节点模板里，
 * 因此匹配到子节点后，用同一份 params 即可解析整条祖先链的 URL。
 */

export function resolveConfig(config) {
  const result = [];

  const travel = (nodes, parentPath, parents, parentLayout, scope) => {
    for (const node of nodes) {
      const pathTemplate = joinPath(parentPath, node.path);
      const layout = node.layout ?? parentLayout;
      const nextScope = node.menuContainer ? node : scope;
      result.push({
        node,
        pathTemplate,
        parents,
        layout,
        scope: nextScope,
        isContainer: !node.component
      });

      if (node.children?.length) {
        travel(node.children, pathTemplate, [...parents, node], layout, nextScope);
      }
    }
  };

  travel(config, '', [], 'base', null);
  return result;
}
/** 取最深（最具体）的精确匹配 */

function findActive(pathname, all) {
  let best = null;

  for (const route of all) {
    const matched = matchPath(pathname, {
      path: route.pathTemplate,
      exact: true,
      strict: false
    });

    if (!matched) {
      continue;
    }

    if (!best || route.pathTemplate.length > best.route.pathTemplate.length) {
      best = {
        route,
        params: matched.params
      };
    }
  }

  return best;
}

function buildMenu(nodes, all, params) {
  return nodes.filter(node => !node.menu?.hide).sort((a, b) => (a.menu?.order ?? 999) - (b.menu?.order ?? 999)).map(node => {
    const resolved = all.find(item => item.node.key === node.key);
    const children = node.children?.length && !node.menuContainer ? buildMenu(node.children, all, params) : undefined;
    return {
      key: node.key,
      name: node.name,
      menu: node.menu,
      component: node.component,
      url: resolved ? toUrl(resolved.pathTemplate, params) : '#',
      children
    };
  }).filter(node => !(node.children && node.children.length === 0));
}
/** 根作用域菜单：未命中任何路由（如 403 兜底页）时仍可展示全局导航 */


export function getRootMenu(all) {
  return buildMenu(all.filter(item => item.parents.length === 0).map(item => item.node), all, {});
}
export function matchConfig(pathname, all) {
  const active = findActive(pathname, all);

  if (!active) {
    return null;
  }

  const {
    route,
    params
  } = active;

  const toMatched = item => ({
    key: item.node.key,
    name: item.node.name,
    pathTemplate: item.pathTemplate,
    url: toUrl(item.pathTemplate, params)
  }); // 菜单来源：命中 menuContainer 节点则用它的 children，否则回到根菜单


  const menuSource = route.scope ? route.scope.children ?? [] : all.filter(item => item.parents.length === 0).map(item => item.node);
  const breadcrumb = [...route.parents, route.node].filter(node => node.breadcrumb !== false && !!node.name).map(node => {
    const resolved = all.find(item => item.node.key === node.key);
    return resolved ? toMatched(resolved) : null;
  }).filter(item => !!item);
  return {
    route,
    params,
    url: toUrl(route.pathTemplate, params),
    menu: buildMenu(menuSource, all, params),
    breadcrumb
  };
}